import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Tabs,
  Tab,
  Grid,
  Avatar,
  IconButton,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setUser } from '../store/slices/authSlice';
import axiosInstance from '../api/axiosConfig';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Phone number formatting utility
const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

const EditProfilePage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]); // For spouse dropdown (non-dependents)
  const [dependents, setDependents] = useState<any[]>([]); // For dependents display
  const [membershipTier, setMembershipTier] = useState<any>(null);

  const [formData, setFormData] = useState({
    // Basic Info
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',

    // Contact
    mobile_phone: '',
    home_phone: '',
    work_phone: '',
    email: '',
    secondary_email: '',
    preferred_contact_method: 'EMAIL',

    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',

    // Mailing Address
    mailing_same_as_home: true,
    mailing_address_line1: '',
    mailing_address_line2: '',
    mailing_city: '',
    mailing_state: '',
    mailing_postal_code: '',
    mailing_country: '',

    // Professional
    occupation: '',
    employer: '',

    // Emergency
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_email: '',
    emergency_contact_relationship: '',

    // Club Preferences
    activity_interests: {
      swim: false,
      tennis: false,
      pickleball: false,
      gym: false,
      yoga: false,
      cycling: false,
      group_classes: false,
      socializing: false,
    },
    dietary_restrictions: '',

    // Privacy
    newsletter_opt_in: false,
    marketing_opt_in: false,
    sms_opt_in: false,
    directory_opt_in: false,

    // Social
    spouse_name: '',
    anniversary_date: '',
  });

  // Helper to determine if user is staff
  const isStaffUser = () => {
    return user?.role && ['STAFF', 'ADMIN', 'SUPERUSER'].includes(user.role.toUpperCase());
  };

  // Fetch fresh user data from API on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Use different endpoint based on user role
        const endpoint = isStaffUser() ? '/staff/me' : '/members/me';
        const response = await axiosInstance.get(endpoint);
        if (response.data.data) {
          dispatch(setUser(response.data.data));
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        date_of_birth: user.date_of_birth || '',
        gender: user.gender || '',
        mobile_phone: user.mobile_phone || '',
        home_phone: user.home_phone || '',
        work_phone: user.work_phone || '',
        email: user.email || '',
        secondary_email: user.secondary_email || '',
        preferred_contact_method: user.preferred_contact_method || 'EMAIL',
        address_line1: user.address_line1 || '',
        address_line2: user.address_line2 || '',
        city: user.city || '',
        state: user.state || '',
        postal_code: user.postal_code || '',
        country: user.country || 'USA',
        mailing_same_as_home: user.mailing_same_as_home !== false,
        mailing_address_line1: user.mailing_address_line1 || '',
        mailing_address_line2: user.mailing_address_line2 || '',
        mailing_city: user.mailing_city || '',
        mailing_state: user.mailing_state || '',
        mailing_postal_code: user.mailing_postal_code || '',
        mailing_country: user.mailing_country || '',
        occupation: user.occupation || '',
        employer: user.employer || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        emergency_contact_email: user.emergency_contact_email || '',
        emergency_contact_relationship: user.emergency_contact_relationship || '',
        activity_interests: user.activity_interests ? {
          swim: user.activity_interests.swim || false,
          tennis: user.activity_interests.tennis || false,
          pickleball: user.activity_interests.pickleball || false,
          gym: user.activity_interests.gym || false,
          yoga: user.activity_interests.yoga || false,
          cycling: user.activity_interests.cycling || false,
          group_classes: user.activity_interests.group_classes || false,
          socializing: user.activity_interests.socializing || false,
        } : {
          swim: false,
          tennis: false,
          pickleball: false,
          gym: false,
          yoga: false,
          cycling: false,
          group_classes: false,
          socializing: false,
        },
        dietary_restrictions: user.dietary_restrictions || '',
        newsletter_opt_in: user.newsletter_opt_in || false,
        marketing_opt_in: user.marketing_opt_in || false,
        sms_opt_in: user.sms_opt_in || false,
        directory_opt_in: user.directory_opt_in || false,
        spouse_name: user.spouse_name || '',
        anniversary_date: user.anniversary_date || '',
      });
    }
  }, [user]);

  // Fetch membership tier and family members
  useEffect(() => {
    const fetchMembershipData = async () => {
      if (!user?.id || !user?.membership_tier_id) return;

      try {
        // Fetch membership tier
        const tierResponse = await axiosInstance.get(`/membership-tiers/${user.membership_tier_id}`);
        if (tierResponse.data.data) {
          setMembershipTier(tierResponse.data.data);
          console.log('Membership tier loaded:', tierResponse.data.data);
        }

        // Fetch all linked members including dependents
        const linkedMembersResponse = await axiosInstance.get('/members/me/linked', {
          params: { include_dependents: true }
        });
        console.log('All linked members response:', linkedMembersResponse.data);

        const allLinkedMembers = linkedMembersResponse.data.data || [];
        console.log('All linked members found:', allLinkedMembers);

        // Separate dependents from non-dependents
        const spouseCandidates = allLinkedMembers.filter((m: any) => m.member_type !== 'DEPENDENT');
        const dependentMembers = allLinkedMembers.filter((m: any) => m.member_type === 'DEPENDENT');

        console.log('Spouse candidates:', spouseCandidates);
        console.log('Dependents:', dependentMembers);

        setFamilyMembers(spouseCandidates);
        setDependents(dependentMembers);
      } catch (err) {
        console.error('Error fetching membership data:', err);
      }
    };

    fetchMembershipData();
  }, [user?.id, user?.membership_tier_id, user?.member_type, user?.primary_member_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value, type, checked } = e.target;

    // Format phone numbers automatically
    const phoneFields = ['mobile_phone', 'home_phone', 'work_phone', 'emergency_contact_phone'];
    const formattedValue = phoneFields.includes(name) ? formatPhoneNumber(value) : value;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : formattedValue
    }));
  };

  const handleActivityChange = (activity: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      activity_interests: {
        ...prev.activity_interests,
        [activity]: e.target.checked
      }
    }));
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please select a PNG, JPG, or GIF image.');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File too large. Maximum size is 5MB.');
        return;
      }

      setSelectedPhoto(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('photo', selectedPhoto);

      const response = await axiosInstance.post('/members/me/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update user in Redux store
      if (response.data.data) {
        dispatch(setUser(response.data.data));
      }

      setSuccess('Profile photo updated successfully!');
      setSelectedPhoto(null);
      setPhotoPreview(null);
    } catch (err: any) {
      console.error('Photo upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use different endpoint based on user role
      const endpoint = isStaffUser() ? '/staff/me' : '/members/me';
      const response = await axiosInstance.put(endpoint, formData);

      // Update user in Redux store
      if (response.data.data) {
        dispatch(setUser(response.data.data));
      }

      setSuccess('Profile updated successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Optionally redirect back to profile after 2 seconds
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit My Profile
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Personal Info" />
            <Tab label="Contact & Address" />
            <Tab label="Emergency Contact" />
            {/* Club Preferences + Privacy tabs removed to trim the profile (2026-06-01). Their
                TabPanels (index 3/4) remain below as unreachable code so re-adding is just restoring
                these two <Tab> labels. See TODO. */}
          </Tabs>
        </Box>

        <form onSubmit={handleSubmit}>
          {/* Tab 1: Personal Info */}
          <TabPanel value={tabValue} index={0}>
            {/* Profile Photo Upload */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                src={photoPreview || (user?.profile_photo_url || undefined)}
                sx={{ width: 150, height: 150, mb: 2 }}
              >
                {!photoPreview && !user?.profile_photo_url && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
              </Avatar>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={handlePhotoSelect}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCamera />}
                  >
                    Select Photo
                  </Button>
                </label>

                {selectedPhoto && (
                  <>
                    <Button
                      variant="contained"
                      onClick={handlePhotoUpload}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                    <IconButton onClick={handleRemovePhoto} disabled={uploadingPhoto}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Allowed: PNG, JPG, GIF (Max 5MB)
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Contact an administrator to change this field"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Contact an administrator to change this field"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Contact an administrator to change this field"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                  >
                    <MenuItem value="">Prefer not to say</MenuItem>
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                    <MenuItem value="PREFER_NOT_TO_SAY">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Occupation + Employer inputs removed to trim the profile (2026-06-01); the fields
                  stay in formData so existing values are preserved on save. See TODO to re-add. */}

              {/* Only show spouse fields for couple/family memberships */}
              {membershipTier && membershipTier.max_family_members > 1 && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {(() => {
                      console.log('Rendering spouse field. Family members:', familyMembers, 'Length:', familyMembers.length);
                      return familyMembers.length > 0 ? (
                        <FormControl fullWidth>
                          <InputLabel>Spouse</InputLabel>
                          <Select
                            name="spouse_name"
                            value={formData.spouse_name}
                            onChange={handleChange}
                            label="Spouse"
                          >
                            <MenuItem value="">None</MenuItem>
                            {familyMembers.map((member) => {
                              console.log('Rendering family member:', member);
                              return (
                                <MenuItem key={member.id} value={`${member.first_name} ${member.last_name}`}>
                                  {member.first_name} {member.last_name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          label="Spouse Name"
                          name="spouse_name"
                          value={formData.spouse_name}
                          onChange={handleChange}
                        />
                      );
                    })()}
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Anniversary Date"
                      name="anniversary_date"
                      type="date"
                      value={formData.anniversary_date}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Dependents Fields - Read-only display, one per dependent */}
                  {dependents.length > 0 && (
                    <>
                      {dependents.map((dependent: any, index: number) => (
                        <Grid size={{ xs: 12, md: 6 }} key={dependent.id || index}>
                          <TextField
                            fullWidth
                            label={`Dependent ${index + 1}`}
                            value={`${dependent.first_name} ${dependent.last_name}${
                              dependent.date_of_birth
                                ? ` - DOB: ${(() => {
                                    const [year, month, day] = dependent.date_of_birth.split('-');
                                    return `${month}/${day}/${year}`;
                                  })()}`
                                : ''
                            }`}
                            InputProps={{
                              readOnly: true,
                            }}
                            helperText={index === 0 ? "Dependents are managed by the primary account holder" : ""}
                          />
                        </Grid>
                      ))}
                    </>
                  )}
                </>
              )}
            </Grid>
          </TabPanel>

          {/* Tab 2: Contact Info */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>Phone Numbers</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Mobile Phone"
                  name="mobile_phone"
                  value={formData.mobile_phone}
                  onChange={handleChange}
                  placeholder="+1-555-0123"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Home Phone"
                  name="home_phone"
                  value={formData.home_phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Work Phone"
                  name="work_phone"
                  value={formData.work_phone}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Email Addresses</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Primary Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                  helperText="Contact admin to change email"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Secondary Email"
                  name="secondary_email"
                  type="email"
                  value={formData.secondary_email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Contact Method</InputLabel>
                  <Select
                    name="preferred_contact_method"
                    value={formData.preferred_contact_method}
                    onChange={handleChange}
                    label="Preferred Contact Method"
                  >
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="PHONE">Phone</MenuItem>
                    <MenuItem value="SMS">SMS</MenuItem>
                    <MenuItem value="MAIL">Mail</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Home Address</Typography>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Checkbox
                  name="mailing_same_as_home"
                  checked={formData.mailing_same_as_home}
                  onChange={handleChange}
                />
              }
              label="Mailing address same as home address"
            />

            {!formData.mailing_same_as_home && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Mailing Address</Typography>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Mailing Address Line 1"
                      name="mailing_address_line1"
                      value={formData.mailing_address_line1}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Mailing Address Line 2"
                      name="mailing_address_line2"
                      value={formData.mailing_address_line2}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="City"
                      name="mailing_city"
                      value={formData.mailing_city}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="State"
                      name="mailing_state"
                      value={formData.mailing_state}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="mailing_postal_code"
                      value={formData.mailing_postal_code}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="mailing_country"
                      value={formData.mailing_country}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </TabPanel>

          {/* Tab 3: Emergency Contact */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Relationship"
                  name="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={handleChange}
                  placeholder="e.g., Spouse, Parent, Sibling"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleChange}
                />
              </Grid>
              {/* Emergency Contact Email input removed to trim the profile (2026-06-01); field kept
                  in formData so existing values persist. See TODO to re-add. */}
            </Grid>
          </TabPanel>

          {/* Tab 4: Club Preferences - Hidden for staff/admin/superuser */}
          {user?.role !== 'staff' && user?.role !== 'admin' && user?.role !== 'superuser' && (
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>Activity Interests</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select the activities you're interested in participating in at the club
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.cycling}
                      onChange={handleActivityChange('cycling')}
                    />
                  }
                  label="Cycling"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.group_classes}
                      onChange={handleActivityChange('group_classes')}
                    />
                  }
                  label="Group Classes"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.gym}
                      onChange={handleActivityChange('gym')}
                    />
                  }
                  label="Gym"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.pickleball}
                      onChange={handleActivityChange('pickleball')}
                    />
                  }
                  label="Pickleball"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.socializing}
                      onChange={handleActivityChange('socializing')}
                    />
                  }
                  label="Socializing"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.swim}
                      onChange={handleActivityChange('swim')}
                    />
                  }
                  label="Swimming"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.tennis}
                      onChange={handleActivityChange('tennis')}
                    />
                  }
                  label="Tennis"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activity_interests.yoga}
                      onChange={handleActivityChange('yoga')}
                    />
                  }
                  label="Yoga"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>Dining Preferences</Typography>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Dietary Restrictions"
                  name="dietary_restrictions"
                  multiline
                  rows={3}
                  value={formData.dietary_restrictions}
                  onChange={handleChange}
                  placeholder="Vegetarian, Vegan, Gluten-free, Allergies, etc."
                />
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Tab 5: Privacy Settings - Hidden for staff/admin/superuser */}
          {user?.role !== 'staff' && user?.role !== 'admin' && user?.role !== 'superuser' && (
          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" gutterBottom>Communication Preferences</Typography>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="newsletter_opt_in"
                    checked={formData.newsletter_opt_in}
                    onChange={handleChange}
                  />
                }
                label="I want to receive the club newsletter"
              />
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    name="marketing_opt_in"
                    checked={formData.marketing_opt_in}
                    onChange={handleChange}
                  />
                }
                label="I want to receive marketing communications and special offers"
              />
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    name="sms_opt_in"
                    checked={formData.sms_opt_in}
                    onChange={handleChange}
                  />
                }
                label="I want to receive SMS/text messages"
              />
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    name="directory_opt_in"
                    checked={formData.directory_opt_in}
                    onChange={handleChange}
                  />
                }
                label="Include my contact info in the member directory"
              />
            </Box>
          </TabPanel>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/profile')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditProfilePage;
